<template>
  <div class="h-listl">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-listl__content">
      <div class="h-listl__head">
        <div>
          <h1 class="h-listl__title">Transactions</h1>
          <p class="h-listl__sub">{{ subtitle }}</p>
        </div>
      </div>

      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load transactions"
        :message="error"
        @retry="load"
      />
      <HermesEmptyState
        v-else-if="!loading && !rows.length"
        variant="first-run"
        title="No transactions yet"
        message="When a customer is charged for one of your plans, every payment, refund and failure appears here."
      />
      <div v-else class="h-listl__card">
        <HermesTabulator
          :rows="rows"
          :columns="columns"
          :loading="loading"
          storage-key="admin-transactions"
          :initial-sort="{ column: 'paid_at', dir: 'desc' }"
        />
      </div>
    </div>

    <!-- ===== Refund modal ===== -->
    <teleport to="body">
      <transition name="h-modal-fade">
        <div v-if="refundModal.open" class="h-scrim" @click.self="closeRefund">
          <div class="h-modal" role="dialog" aria-modal="true" aria-label="Refund transaction" @keydown.esc.prevent="closeRefund">
            <div class="h-modal__head">
              <div class="h-modal__icon h-modal__icon--danger">
                <svg viewBox="0 0 24 24"><path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-9 9z"/><path d="M9 12l2 2 4-4"/></svg>
              </div>
              <div class="h-modal__headtext">
                <h3 class="h-modal__title">Refund transaction #{{ refundModal.row?.id }}</h3>
                <p class="h-modal__desc">
                  Refunds go back through <strong>{{ gatewayLabel(refundModal.row?.gateway) }}</strong> to the customer's
                  original payment method. This can't be undone.
                </p>
              </div>
              <button type="button" class="h-modal__x" aria-label="Close" @click="closeRefund">✕</button>
            </div>

            <div class="h-modal__body">
              <div class="h-refund-summary">
                <div><span>Charged</span><b>{{ moneyPlain(refundModal.total, refundModal.currency) }}</b></div>
                <div v-if="refundModal.alreadyRefunded > 0"><span>Already refunded</span><b>{{ moneyPlain(refundModal.alreadyRefunded, refundModal.currency) }}</b></div>
                <div class="h-refund-summary__rem"><span>Refundable now</span><b>{{ moneyPlain(refundModal.remaining, refundModal.currency) }}</b></div>
              </div>

              <label class="h-field">
                <span class="h-field__label">Amount to refund</span>
                <div class="h-field__amount">
                  <span class="h-field__cur">{{ currencySymbol(refundModal.currency) }}</span>
                  <input
                    ref="amountInput"
                    v-model="refundModal.amount"
                    class="h-field__input"
                    type="number"
                    step="0.01"
                    min="0.01"
                    :max="refundModal.remaining"
                    @keydown.enter="doRefund"
                  />
                  <button type="button" class="h-field__max" @click="refundModal.amount = String(refundModal.remaining)">Full</button>
                </div>
                <span v-if="amountError" class="h-field__err">{{ amountError }}</span>
                <span v-else class="h-field__hint">Enter a smaller amount for a partial refund.</span>
              </label>

              <label class="h-field">
                <span class="h-field__label">Reason <em>(optional)</em></span>
                <input
                  v-model="refundModal.reason"
                  class="h-field__input h-field__input--text"
                  type="text"
                  maxlength="500"
                  placeholder="e.g. Customer request, duplicate charge"
                  @keydown.enter="doRefund"
                />
              </label>
            </div>

            <div class="h-modal__foot">
              <button type="button" class="h-btn h-btn--secondary" @click="closeRefund">Cancel</button>
              <button
                type="button"
                class="h-btn h-btn--danger"
                :disabled="refundModal.busy || !!amountError || !(Number(refundModal.amount) > 0)"
                @click="doRefund"
              >
                {{ refundModal.busy ? 'Refunding…' : `Refund ${moneyPlain(Number(refundModal.amount) || 0, refundModal.currency)}` }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- ===== Toasts ===== -->
    <div class="h-toasts" aria-live="polite">
      <div v-for="t in toasts" :key="t.id" class="h-toast" :class="`h-toast--${t.kind}`">{{ t.text }}</div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesTransactionsList — Phase 5 lean transactions list island. Replaces the old
// 6-tab dashboard's `transactions` tab: a HermesTabulator over GET
// /admin/transactions. Money/status/gateway are formatted inline (Tabulator cells
// render outside scoped CSS).
//
// Plan 007 money-moving surface: a per-row Refund action opens a modal (amount
// prefilled with the remaining refundable balance; partial allowed) that POSTs
// /admin/transactions/{id}/refund. On success the list refreshes to show the new
// refunded amount + status badge. The gateway-first backend reconciles with the
// incoming payment.refunded webhook (RefundService).
export default {
  name: 'HermesTransactionsList',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    vendorName: { type: String, default: '' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      rows: [],
      loading: true,
      error: null,
      crumbs: [{ label: 'Commerce' }, { label: 'Transactions' }],
      toasts: [],
      toastSeq: 0,
      refundModal: {
        open: false,
        busy: false,
        row: null,
        amount: '',
        reason: '',
        total: 0,
        alreadyRefunded: 0,
        remaining: 0,
        currency: 'GBP',
      },
      columns: [
        { key: 'id', label: 'ID', width: 72, format: (v) => `<span style="font-family:var(--h-font-mono);color:var(--h-text-secondary)">#${v}</span>` },
        { key: 'end_user_email', label: 'Customer', format: (v) => v || '<span style="color:var(--h-text-tertiary)">—</span>' },
        { key: 'gateway', label: 'Gateway', width: 120, format: (v) => this.gateway(v) },
        { key: 'status', label: 'Status', width: 170, format: (v, row) => this.statusCell(v, row) },
        { key: 'amount_total', label: 'Amount', hozAlign: 'right', width: 110, sorter: 'number', format: (v, row) => this.money(v, row.currency) },
        { key: 'amount_vat', label: 'VAT', hozAlign: 'right', width: 130, sorter: 'number', format: (v, row) => this.vatCell(v, row) },
        { key: 'amount_refunded', label: 'Refunded', hozAlign: 'right', width: 110, sorter: 'number', format: (v, row) => this.refundedCell(v, row) },
        { key: 'description', label: 'Description', format: (v) => v || '<span style="color:var(--h-text-tertiary)">—</span>' },
        { key: 'paid_at', label: 'Paid', width: 120, sorter: 'number', format: (v) => this.date(v) },
        { key: '_actions', label: '', width: 108, headerFilter: false, sorter: false, hozAlign: 'right', format: (v, row) => this.actionCell(row), cellClick: (row) => this.onActionClick(row) },
      ],
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const n = this.rows.length;
      const ok = this.rows.filter((r) => r.status === 'succeeded').length;
      return `${n} transaction${n === 1 ? '' : 's'} · ${ok} succeeded`;
    },
    amountError() {
      if (!this.refundModal.open) return '';
      const amt = Number(this.refundModal.amount);
      if (this.refundModal.amount === '' || Number.isNaN(amt)) return 'Enter an amount.';
      if (amt <= 0) return 'Amount must be greater than zero.';
      // Allow a cent of float slack so "Full" always validates.
      if (amt > this.refundModal.remaining + 0.005) return `Can't exceed the ${this.moneyPlain(this.refundModal.remaining, this.refundModal.currency)} refundable.`;
      return '';
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/transactions`);
        this.rows = (data.items || []).map((r) => ({
          ...r,
          amount_total: r.amount_total != null ? Number(r.amount_total) : null,
          amount_refunded: r.amount_refunded != null ? Number(r.amount_refunded) : 0,
          amount_vat: r.amount_vat != null ? Number(r.amount_vat) : null,
        }));
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load transactions${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    // ── Refund flow ───────────────────────────────────────────────────
    isRefundable(row) {
      const refunded = Number(row.amount_refunded) || 0;
      const total = Number(row.amount_total) || 0;
      const pending = Number(row.amount_refund_pending) || 0;
      return ['succeeded', 'partially_refunded'].includes(row.status) && total - refunded - pending > 0.005;
    },
    onActionClick(row) {
      if (this.isRefundable(row)) this.openRefund(row);
    },
    openRefund(row) {
      const total = Number(row.amount_total) || 0;
      const refunded = Number(row.amount_refunded) || 0;
      const pending = Number(row.amount_refund_pending) || 0;
      const remaining = Math.max(0, Math.round((total - refunded - pending) * 100) / 100);
      this.refundModal = {
        open: true,
        busy: false,
        row,
        amount: String(remaining),
        reason: '',
        total,
        alreadyRefunded: refunded + pending,
        remaining,
        currency: row.currency || 'GBP',
      };
      this.$nextTick(() => { if (this.$refs.amountInput) this.$refs.amountInput.focus(); });
    },
    closeRefund() {
      if (this.refundModal.busy) return;
      this.refundModal.open = false;
    },
    async doRefund() {
      if (this.amountError || this.refundModal.busy) return;
      const { row, remaining } = this.refundModal;
      const amt = Number(this.refundModal.amount);
      const body = {};
      // A full-remaining refund omits `amount` (the gateway refunds the balance);
      // anything less is sent as an explicit partial.
      if (amt < remaining - 0.005) body.amount = amt;
      const reason = this.refundModal.reason.trim();
      if (reason) body.reason = reason;

      this.refundModal.busy = true;
      try {
        const res = await this.api.post(`${this.apiBase}/admin/transactions/${row.id}/refund`, body);
        const r = res?.refund || {};
        const amount = r.amount_refunded ?? this.refundModal.amount;
        const cur = r.currency || this.refundModal.currency;
        const full = (r.status || '') === 'refunded';
        this.refundModal.open = false;
        this.toast(
          r.refund_status === 'pending'
            ? `Refund of ${this.moneyPlain(Number(amount), cur)} sent. It shows as refunded once the provider confirms it.`
            : `Refunded ${this.moneyPlain(Number(amount), cur)} on #${row.id}${full ? ' — fully refunded' : ' (partial)'}.`,
          'success',
        );
        await this.load();
      } catch (e) {
        const status = e?.response?.status;
        const data = e?.response?.data || {};
        const msg = data.message || e?.message || 'Unknown error';
        if (status === 422) this.toast(`Refund rejected: ${msg}`, 'error');
        else if (status === 502) this.toast(`Gateway error — refund not applied. ${msg}`, 'error');
        else if (status === 404) this.toast('Transaction not found.', 'error');
        else this.toast(`Refund failed: ${msg}`, 'error');
      } finally {
        this.refundModal.busy = false;
      }
    },
    toast(text, kind = 'success') {
      const id = ++this.toastSeq;
      this.toasts.push({ id, text, kind });
      setTimeout(() => { this.toasts = this.toasts.filter((t) => t.id !== id); }, 5200);
    },
    // ── Cell formatters ───────────────────────────────────────────────
    actionCell(row) {
      if (!this.isRefundable(row)) return '<span style="color:var(--h-text-tertiary)">—</span>';
      return `<button type="button" class="h-rowbtn" style="font:inherit;font-size:12px;font-weight:600;padding:4px 11px;border-radius:6px;border:1px solid var(--h-danger-border,#FECACA);background:var(--h-danger-bg,#FEF2F2);color:var(--h-danger-text,#B91C1C);cursor:pointer">Refund</button>`;
    },
    // The VAT recorded when the payment was made, with its rate. A payment from
    // before VAT handling, or one that did not succeed, has none.
    vatCell(v, row) {
      if (v === null || v === undefined) {
        const muted = 'color:var(--h-text-tertiary);font-size:12px';
        return ['succeeded', 'refunded', 'partially_refunded'].includes(row.status)
          ? `<span style="${muted}" title="Made before Hermes recorded VAT">not recorded</span>`
          : `<span style="${muted}">—</span>`;
      }
      const rate = row.vat_rate != null ? ` <span style="color:var(--h-text-tertiary);font-size:11.5px">${Number(row.vat_rate)}%</span>` : '';
      return `<span style="font-family:var(--h-font-mono);font-variant-numeric:tabular-nums">${this.moneyPlain(v, row.currency)}</span>${rate}`;
    },
    // A refund is only `refunded` once the provider confirms it.
    statusCell(v, row) {
      if (row.refund_failure) return this.badge('refund_failed', 'danger', String(row.refund_failure).replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`));
      return Number(row.amount_refund_pending) > 0 ? this.badge('refund_pending', 'info') : this.badge(v);
    },
    refundedCell(v, row) {
      const n = Number(v) || 0;
      const pending = Number(row.amount_refund_pending) || 0;
      if (pending > 0) {
        const done = n > 0 ? `${this.moneyPlain(n, row.currency)} + ` : '';
        return `<span style="font-family:var(--h-font-mono);font-variant-numeric:tabular-nums;color:var(--h-text-secondary,#64748B)">${done}${this.moneyPlain(pending, row.currency)} pending</span>`;
      }
      if (n <= 0) return '<span style="color:var(--h-text-tertiary)">—</span>';
      const partial = row.status === 'partially_refunded';
      const color = partial ? 'var(--h-warning-text,#B45309)' : 'var(--h-text-secondary,#64748B)';
      return `<span style="font-family:var(--h-font-mono);font-variant-numeric:tabular-nums;color:${color}">${this.moneyPlain(n, row.currency)}</span>`;
    },
    money(v, currency) {
      if (v === null || v === undefined || v === '') return '<span style="color:var(--h-text-tertiary)">—</span>';
      const n = Number(v);
      const txt = `${this.currencySymbol(currency)}${Number.isNaN(n) ? v : n.toFixed(2)}`;
      return `<span style="font-family:var(--h-font-mono);font-variant-numeric:tabular-nums">${txt}</span>`;
    },
    moneyPlain(v, currency) {
      const n = Number(v) || 0;
      return `${this.currencySymbol(currency)}${n.toFixed(2)}`;
    },
    currencySymbol(currency) {
      return { GBP: '£', USD: '$', EUR: '€' }[currency] || '';
    },
    date(v) {
      if (!v) return '<span style="color:var(--h-text-tertiary)">—</span>';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    gateway(g) {
      const color = { stripe: '#635BFF', revolut: '#191C1F', create_pay: '#0EA5E9', createpay: '#0EA5E9' };
      const key = (g || '').toLowerCase();
      const c = color[key] || 'var(--h-text-tertiary)';
      return `<span style="display:inline-flex;align-items:center;gap:6px;font-size:12.5px;color:var(--h-text-secondary)"><i style="width:8px;height:8px;border-radius:2px;background:${c}"></i>${this.gatewayLabel(g)}</span>`;
    },
    gatewayLabel(g) {
      const map = { stripe: 'Stripe', revolut: 'Revolut', create_pay: 'Create Pay', createpay: 'Create Pay' };
      const key = (g || '').toLowerCase();
      return map[key] || g || '—';
    },
    badge(status, forced = null, title = '') {
      const map = { succeeded: 'success', paid: 'success', pending: 'info', processing: 'info', failed: 'danger', refunded: 'neutral', partially_refunded: 'warning', cancelled: 'neutral' };
      const role = forced || map[status] || 'neutral';
      const s = (status || '').replace(/_/g, ' ');
      return `<span title="${title}" style="display:inline-flex;align-items:center;gap:6px;padding:3px 9px;border-radius:999px;font-size:11.5px;font-weight:600;border:1px solid var(--h-${role}-border);background:var(--h-${role}-bg);color:var(--h-${role}-text)">`
        + `<span style="width:6px;height:6px;border-radius:50%;background:var(--h-${role})"></span>${s}</span>`;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-listl { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-listl__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-listl__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-listl__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-listl__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-listl__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-listl__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-listl__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }

/* ===== Refund modal ===== */
.h-scrim { position: fixed; inset: 0; z-index: 1300; background: var(--h-bg-overlay, rgba(15, 23, 42, 0.55)); display: flex; align-items: center; justify-content: center; padding: 24px; }
.h-modal { width: 100%; max-width: 440px; background: var(--h-bg-card, #fff); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-pop); border: 1px solid var(--h-border, #E2E8F0); overflow: hidden; }
.h-modal__head { padding: 20px 22px 0; display: flex; gap: 14px; align-items: flex-start; }
.h-modal__icon { flex: none; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
.h-modal__icon svg { width: 20px; height: 20px; stroke: currentColor; fill: none; stroke-width: 2; }
.h-modal__icon--danger { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); }
.h-modal__headtext { flex: 1; }
.h-modal__title { font-size: 16px; font-weight: 600; margin: 0 0 4px; color: var(--h-text-primary, #0F172A); }
.h-modal__desc { font-size: 13px; color: var(--h-text-secondary, #64748B); margin: 0; line-height: 1.45; }
.h-modal__x { margin-left: auto; border: none; background: none; color: var(--h-text-tertiary, #94A3B8); cursor: pointer; font-size: 18px; line-height: 1; padding: 2px; }
.h-modal__x:hover { color: var(--h-text-primary, #0F172A); }
.h-modal__body { padding: 16px 22px 4px; display: flex; flex-direction: column; gap: 14px; }

.h-refund-summary { display: flex; flex-direction: column; gap: 6px; background: var(--h-bg-subtle, #F1F5F9); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); padding: 11px 13px; font-size: 13px; }
.h-refund-summary > div { display: flex; justify-content: space-between; }
.h-refund-summary span { color: var(--h-text-secondary, #64748B); }
.h-refund-summary b { font-family: var(--h-font-mono, monospace); font-variant-numeric: tabular-nums; color: var(--h-text-primary, #0F172A); }
.h-refund-summary__rem b { color: var(--h-danger-text, #B91C1C); }

.h-field { display: flex; flex-direction: column; gap: 6px; }
.h-field__label { font-size: 12.5px; font-weight: 600; color: var(--h-text-secondary, #64748B); }
.h-field__label em { font-weight: 400; font-style: normal; color: var(--h-text-tertiary, #94A3B8); }
.h-field__amount { display: flex; align-items: center; gap: 0; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); overflow: hidden; background: var(--h-bg-card, #fff); }
.h-field__amount:focus-within { border-color: var(--h-danger, #EF4444); box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15); }
.h-field__cur { padding: 0 4px 0 12px; color: var(--h-text-secondary, #64748B); font-family: var(--h-font-mono, monospace); }
.h-field__input { flex: 1; font: inherit; font-size: 14px; padding: 9px 6px; border: none; outline: none; background: transparent; color: var(--h-text-primary, #0F172A); font-variant-numeric: tabular-nums; }
.h-field__input--text { border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); padding: 9px 12px; }
.h-field__input--text:focus { border-color: var(--h-accent, #6366F1); box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14); }
.h-field__max { border: none; border-left: 1px solid var(--h-border, #E2E8F0); background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #64748B); font: inherit; font-size: 12px; font-weight: 600; padding: 0 12px; align-self: stretch; cursor: pointer; }
.h-field__max:hover { color: var(--h-text-primary, #0F172A); }
.h-field__hint { font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); }
.h-field__err { font-size: 11.5px; color: var(--h-danger-text, #B91C1C); }

.h-modal__foot { display: flex; justify-content: flex-end; gap: 10px; padding: 18px 22px 20px; }
.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; }
.h-btn--secondary { background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A); border-color: var(--h-border-strong, #CBD5E1); }
.h-btn--secondary:hover { background: var(--h-bg-subtle, #F1F5F9); }
.h-btn--danger { background: var(--h-danger, #EF4444); color: #fff; }
.h-btn--danger:hover:not(:disabled) { background: var(--h-danger-hover, #DC2626); }
.h-btn--danger:disabled { opacity: 0.5; cursor: not-allowed; }

.h-modal-fade-enter-active, .h-modal-fade-leave-active { transition: opacity 0.16s ease; }
.h-modal-fade-enter-from, .h-modal-fade-leave-to { opacity: 0; }

/* ===== Toasts ===== */
.h-toasts { position: fixed; bottom: 24px; right: 24px; z-index: 1400; display: flex; flex-direction: column; gap: 10px; max-width: 420px; }
.h-toast { padding: 11px 16px; border-radius: var(--h-radius, 6px); font-size: 13px; font-weight: 500; box-shadow: var(--h-shadow-panel); border: 1px solid transparent; }
.h-toast--success { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); border-color: var(--h-success-border, #A7F3D0); }
.h-toast--error { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); border-color: var(--h-danger-border, #FECACA); }
</style>
